<template>
  <div>
    <app-navigation />
    <main class="admin-page">
      <div class="admin-heading">
        <span class="admin-kicker">Release lifecycle</span>
        <h1>Builds & deployments</h1>
        <p>
          Every build shows the immutable template version it captured.
          Activating or rolling back publishes routing without changing content.
        </p>
      </div>
      <div v-if="error" class="admin-alert bad">{{ error }}</div>
      <div v-if="notice" class="admin-alert good">{{ notice }}</div>
      <section class="admin-card">
        <label
          >Authorized site<select v-model="site" @change="load">
            <option :value="root.id">{{ root.name }}</option>
            <option v-for="child in children" :key="child.id" :value="child.id">
              {{ child.name }}
            </option>
          </select></label
        >
        <div class="admin-context">
          <strong>{{ siteName }}</strong
          ><code>{{ site }}</code>
        </div>
        <div class="admin-row">
          <button class="admin-primary" @click="queue">Queue build</button
          ><button class="admin-secondary" @click="prune">
            Prune unused builds</button
          ><button class="admin-danger" @click="deactivate">
            Take offline
          </button>
        </div>
      </section>
      <section class="admin-grid">
        <div class="admin-card">
          <h2>Builds</h2>
          <div class="admin-list">
            <article
              v-for="build in builds"
              :key="build.id"
              :class="{ selected: selectedBuild === build.id }"
              @click="selectedBuild = build.id"
            >
              <div>
                <strong>{{ build.id.slice(0, 12) }}</strong
                ><small>{{ versionLabel(build.template_version_id) }}</small
                ><small
                  >{{ build.status }} ·
                  {{
                    build.artifact_digest || build.failure_reason || "waiting"
                  }}</small
                >
              </div>
              <button
                class="admin-danger"
                :disabled="['queued', 'running'].includes(build.status)"
                @click.stop="removeBuild(build)"
              >
                Delete
              </button>
            </article>
          </div>
          <button
            class="admin-primary"
            :disabled="!deployable"
            @click="publish"
          >
            Publish selected successful build
          </button>
        </div>
        <div class="admin-card">
          <h2>Deployments</h2>
          <div class="admin-list">
            <article v-for="deployment in deployments" :key="deployment.id">
              <div>
                <strong>{{ deployment.id.slice(0, 12) }}</strong
                ><small>{{ deploymentVersion(deployment) }}</small
                ><small>{{ deployment.status }}</small>
              </div>
              <button class="admin-secondary" @click="activate(deployment)">
                Activate</button
              ><button
                class="admin-danger"
                :disabled="deployment.status === 'active'"
                @click="removeDeployment(deployment)"
              >
                Delete
              </button>
            </article>
          </div>
          <button class="admin-secondary" @click="rollback">Rollback</button>
        </div>
      </section>
    </main>
  </div>
</template>
<script>
import { session } from "../platform/session";
import * as api from "../platform/api";
export default {
  name: "BuildsDeployments",
  data() {
    return {
      root: session.root,
      children: [],
      versions: [],
      site: session.root.id,
      builds: [],
      deployments: [],
      selectedBuild: "",
      error: "",
      notice: "",
      timer: null,
    };
  },
  computed: {
    siteName() {
      return (
        [this.root, ...this.children].find((v) => v.id === this.site)?.name ||
        this.site
      );
    },
    deployable() {
      return (
        this.builds.find((v) => v.id === this.selectedBuild)?.status ===
        "succeeded"
      );
    },
  },
  async created() {
    const [children, catalog] = await Promise.all([
      api.children(this.root.id, this.root.id),
      api.authorizedTemplates(this.root.id),
    ]);
    this.children = children;
    this.versions = catalog.items.flatMap((template) =>
      (template.versions || []).map((version) => ({
        ...version,
        template_name: template.name,
      }))
    );
    await this.load();
    this.timer = setInterval(() => {
      if (this.builds.some((v) => ["queued", "running"].includes(v.status)))
        this.load(true);
    }, 3000);
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
  methods: {
    versionLabel(id) {
      if (!id) return "No template version";
      const version = this.versions.find((row) => row.id === id);
      return version
        ? `${version.template_name} · ${version.version}`
        : `Version ${id.slice(0, 12)}`;
    },
    deploymentVersion(deployment) {
      return this.versionLabel(
        this.builds.find((build) => build.id === deployment.build_id)
          ?.template_version_id
      );
    },
    async perform(fn, message, quiet = false) {
      if (!quiet) {
        this.error = "";
        this.notice = "";
      }
      try {
        await fn();
        if (!quiet) this.notice = message;
      } catch (e) {
        if (!quiet) this.error = e.message;
      }
    },
    async route() {
      try {
        await api.publishRouting(this.site, this.root.id);
      } catch (e) {
        if (e.status !== 409) throw e;
      }
    },
    async load(quiet = false) {
      await this.perform(
        async () => {
          const [b, d] = await Promise.all([
            api.builds(this.site, this.root.id),
            api.deployments(this.site, this.root.id),
          ]);
          this.builds = b.items;
          this.deployments = d.items;
        },
        "Pipeline loaded",
        quiet
      );
    },
    queue() {
      return this.perform(async () => {
        await api.createBuild(this.site, this.root.id);
        await this.load(true);
      }, "Build queued");
    },
    publish() {
      return this.perform(async () => {
        await api.createDeployment(this.site, this.root.id, this.selectedBuild);
        await this.load(true);
      }, "Deployment published");
    },
    activate(d) {
      return this.perform(async () => {
        await api.activateDeployment(this.site, this.root.id, d.id);
        await this.route();
        await this.load(true);
      }, "Deployment activated and routing queued. Seed initial content explicitly from Content.");
    },
    rollback() {
      return this.perform(async () => {
        await api.rollbackDeployment(this.site, this.root.id);
        await this.route();
        await this.load(true);
      }, "Rolled back and routing queued; existing content was preserved");
    },
    deactivate() {
      if (!confirm("Take this site offline?")) return;
      return this.perform(async () => {
        await api.deactivateDeployment(this.site, this.root.id);
        await this.route();
        await this.load(true);
      }, "Deactivated and routing publication queued");
    },
    prune() {
      return this.perform(async () => {
        const r = await api.pruneBuilds(this.site, this.root.id);
        await this.load(true);
        this.notice = `${r.deleted} builds pruned`;
      }, "Pruned");
    },
    removeBuild(b) {
      return this.perform(async () => {
        await api.deleteBuild(this.site, this.root.id, b.id);
        await this.load(true);
      }, "Build deleted");
    },
    removeDeployment(d) {
      return this.perform(async () => {
        await api.deleteDeployment(this.site, this.root.id, d.id);
        await this.load(true);
      }, "Deployment deleted");
    },
  },
};
</script>
